import * as Phaser from 'phaser';
import { PlayerStats } from '../components/PlayerStats';
import { createLogger } from '../utils/Logger';

export interface MovementVectorProvider {
    getMovementVector(): { x: number; y: number };
}

/**
 * Система управления перемещением игрока
 */
export class PlayerController {
    private scene: Phaser.Scene;
    private player: Phaser.Physics.Arcade.Sprite;
    private stats: PlayerStats | null = null;
    private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
    private wasd: any = null;
    private defaultSpeed: number = 200;
    private movementVectorProvider: MovementVectorProvider | null = null;
    private lastInputMode: 'idle' | 'keyboard' | 'touch' = 'idle';
    private readonly logger = createLogger('PlayerController');

    constructor(scene: Phaser.Scene, player: Phaser.Physics.Arcade.Sprite, stats?: PlayerStats) {
        this.scene = scene;
        this.player = player;
        this.stats = stats || null;

        if (this.scene.input && this.scene.input.keyboard) {
            this.cursors = this.scene.input.keyboard.createCursorKeys();
            this.wasd = this.scene.input.keyboard.addKeys('W,A,S,D');
        }
    }

    /**
     * Обновляет перемещение игрока на основе ввода
     */
    public update(): void {
        if (!this.player || !this.player.body) return;

        let velocityX = 0;
        let velocityY = 0;
        let inputMode: 'idle' | 'keyboard' | 'touch' = 'idle';

        const speed = this.stats ? this.stats.get('moveSpeed') : this.defaultSpeed;
        const touchVector = this.movementVectorProvider?.getMovementVector();
        if (touchVector && (Math.abs(touchVector.x) > 0.001 || Math.abs(touchVector.y) > 0.001)) {
            velocityX = touchVector.x * speed;
            velocityY = touchVector.y * speed;
            inputMode = 'touch';
        }

        // Чтение ввода WASD
        if (inputMode !== 'touch' && this.wasd) {
            if (this.wasd.A.isDown) velocityX = -speed;
            else if (this.wasd.D.isDown) velocityX = speed;

            if (this.wasd.W.isDown) velocityY = -speed;
            else if (this.wasd.S.isDown) velocityY = speed;

            if (velocityX !== 0 || velocityY !== 0) {
                inputMode = 'keyboard';
            }
        }

        // Чтение стрелок, если WASD не нажат
        if (inputMode === 'idle' && this.cursors && velocityX === 0 && velocityY === 0) {
            if (this.cursors.left.isDown) velocityX = -speed;
            else if (this.cursors.right.isDown) velocityX = speed;

            if (this.cursors.up.isDown) velocityY = -speed;
            else if (this.cursors.down.isDown) velocityY = speed;

            if (velocityX !== 0 || velocityY !== 0) {
                inputMode = 'keyboard';
            }
        }

        // Нормализация диагонального движения для цифрового keyboard input.
        // Touch provider already supplies an analog vector with capped magnitude.
        if (inputMode === 'keyboard' && velocityX !== 0 && velocityY !== 0) {
            velocityX *= Math.SQRT1_2;
            velocityY *= Math.SQRT1_2;
        }

        this.player.setVelocity(velocityX, velocityY);
        this.logInputModeTransition(inputMode);
    }

    public setStats(stats: PlayerStats): void {
        this.stats = stats;
    }

    public setMovementVectorProvider(provider: MovementVectorProvider | null): void {
        this.movementVectorProvider = provider;
        this.logger.debug('setMovementVectorProvider', { hasProvider: provider !== null });
    }

    private logInputModeTransition(inputMode: 'idle' | 'keyboard' | 'touch'): void {
        if (this.lastInputMode === inputMode) return;
        this.logger.debug('input-mode:changed', {
            previous: this.lastInputMode,
            next: inputMode
        });
        this.lastInputMode = inputMode;
    }
}
